import type { SVGAttributes } from "react";

import CheckmarkCircleIcon from "$/lib/icons/CheckmarkCircleIcon";
import CloseIconOutline from "$/lib/icons/CloseIconOutline";
import CrossCircleIcon from "$/lib/icons/CrossCircleIcon";
import CSVFileIcon from "$/lib/icons/CSVFileIcon";
import ImageFileIcon from "$/lib/icons/ImageFileIcon";
import LoadingCircularIcon from "$/lib/icons/LoadingCircularIcon";
import PDFFileIcon from "$/lib/icons/PDFFileIcon";
import TrashIcon from "$/lib/icons/TrashIcon";
import VideoFileIcon from "$/lib/icons/VideoFileIcon";
import XLSXFileIcon from "$/lib/icons/XLSXFileIcon";
import ZIPFileIcon from "$/lib/icons/ZIPFileIcon";
import { convertTohHumanFileSize } from "$/lib/utils/functions/misc.functions";

import { ProgressBar } from "../../atoms";
import { type FileItemProps, FileUploaderStatus } from "./FileUploader.types";

const FileItem = ({
  file,
  currentUploadedSize,
  status = FileUploaderStatus.UPLOADING,
  onCancel = () => undefined,
  onDelete = () => undefined,
}: FileItemProps) => {
  const handleCancel = () => {
    onCancel();
  };

  const handleDelete = () => {
    onDelete();
  };

  const className: SVGAttributes<SVGSVGElement>["className"] =
    "h-9 w-10 shrink-0 sm:h-[60px] sm:w-16";

  const fileTypeIconMapper: Record<string, JSX.Element> = {
    "application/pdf": <PDFFileIcon className={className} />,
    "text/csv": <CSVFileIcon className={className} />,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": (
      <XLSXFileIcon className={className} />
    ),
    "application/vnd.ms-excel": <XLSXFileIcon className={className} />,
    "application/zip": <ZIPFileIcon className={className} />,
    "image/jpeg": <ImageFileIcon className={className} />,
    "image/png": <ImageFileIcon className={className} />,
    "image/jpg": <ImageFileIcon className={className} />,
    "image/gif": <ImageFileIcon className={className} />,
    "image/svg": <ImageFileIcon className={className} />,
    "image/webp": <ImageFileIcon className={className} />,
    "video/mp4": <VideoFileIcon className={className} />,
    "video/ogg": <VideoFileIcon className={className} />,
    "video/webm": <VideoFileIcon className={className} />,
  };

  return (
    <div className="flex w-full flex-col gap-8 rounded-3xl bg-tertiary-100 p-8">
      <div className="flex gap-6">
        {fileTypeIconMapper[file.type]}
        <div className="flex flex-col items-start gap-2">
          <p className="font-bold text-black sm:text-lg">{file.name}</p>
          <p className="hidden text-xs font-semibold sm:inline-block sm:text-sm">
            <span className="text-tertiary-500">
              {convertTohHumanFileSize(currentUploadedSize)} of{" "}
              {convertTohHumanFileSize(file.size)} •
            </span>
            {status === FileUploaderStatus.UPLOADING && (
              <>
                <LoadingCircularIcon className="mx-1 inline-block animate-spin" />
                <span className="text-black">Uploading...</span>
              </>
            )}
            {status === FileUploaderStatus.COMPLETED && (
              <>
                <CheckmarkCircleIcon className="mx-1 inline-block" />
                <span className="text-black">Completed</span>
              </>
            )}
            {status === FileUploaderStatus.FAILED && (
              <>
                <CrossCircleIcon className="mx-1 inline-block" />
                <span className="text-black">Completed</span>
              </>
            )}
          </p>
        </div>
        {status === "uploading" ? (
          <CloseIconOutline
            className="ml-auto shrink-0 cursor-pointer"
            onClick={handleCancel}
          />
        ) : (
          <TrashIcon
            className="ml-auto shrink-0 cursor-pointer"
            onClick={handleDelete}
          />
        )}
      </div>
      <p className="text-xs font-semibold sm:hidden">
        <span className="text-tertiary-500">
          {convertTohHumanFileSize(currentUploadedSize)} of{" "}
          {convertTohHumanFileSize(file.size)} •
        </span>
        {status === FileUploaderStatus.UPLOADING && (
          <>
            <LoadingCircularIcon className="mx-1 inline-block animate-spin" />
            <span className="text-black">Uploading...</span>
          </>
        )}
        {status === FileUploaderStatus.COMPLETED && (
          <>
            <CheckmarkCircleIcon className="mx-1 inline-block" />
            <span className="text-black">Completed</span>
          </>
        )}
        {status === FileUploaderStatus.FAILED && (
          <>
            <CrossCircleIcon className="mx-1 inline-block" />
            <span className="text-black">Completed</span>
          </>
        )}
      </p>
      {status === "uploading" && (
        <ProgressBar
          value={(currentUploadedSize / file.size) * 100}
          progressClassName="bg-primary-400"
        />
      )}
    </div>
  );
};

export default FileItem;
